import React, { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { useTranslate } from 'hooks/use-translate';
import { SectionPaper } from '../../../section-papper';
import {
  apiFormDocumentCategories,
  FormDocumentCategoryType,
} from 'services/form-document-categories';
import { useEffectError } from 'hooks';
import style from './index.module.scss';
import { CategoryItem } from './components';
import { Loading } from 'AurionCR/components';
import { IconButton, List, Tooltip } from '@material-ui/core';
import { NativeScroll } from 'components/native-scroll';
import { apiUserPatientProfileDocuments } from 'services/user-patient-profile-documents';
import {
  apiPatientFormDocuments,
  ServicePatientFormDocuments,
} from 'services/user-patient-profile-form-documents';
import { Unset } from 'utils/types';
import CloseIcon from '@material-ui/icons/Close';

const ICON_DOCUMENT = process.env.PUBLIC_URL + '/images/icon-no-signed-documents-categories.png';

interface Props {
  prescriptionID: string;
  userPatientProfileID: string | null | undefined;
  className: string;
}

export const SignedDocumentsCategories: React.FC<Props> = ({
  prescriptionID,
  userPatientProfileID,
  className,
}) => {
  const { t, tp } = useTranslate();
  const resultCategories = apiFormDocumentCategories.useGetFormDocumentCategoriesTabSignedQuery();
  useEffectError(resultCategories.error);

  const resultSignedDocs =
    apiPatientFormDocuments.useGetUserPatientProfileDocumentsAllAlreadySignedQuery(
      userPatientProfileID || '',
      { skip: !userPatientProfileID },
    );
  useEffectError(resultSignedDocs.error);

  const signedDocs = useMemo(() => {
    // exclude current prescription
    return (resultSignedDocs.data || []).filter(
      (doc) => doc.userPatientProfilePrescriptionID !== prescriptionID,
    );
  }, [resultSignedDocs.data, prescriptionID]);

  const resultFromOldSystem =
    apiUserPatientProfileDocuments.useGetUserPatientProfileDocumentsFromOldSystemQuery(
      userPatientProfileID || '',
      {
        skip: !userPatientProfileID,
      },
    );
  useEffectError(resultFromOldSystem.error);
  const oldDocuments = useMemo(() => {
    return (resultFromOldSystem.data || []).map((doc) => ({
      title: doc.documentType.title || 'unknown',
      date: doc.createdDate || '',
      url: doc.documentURL || '',
    }));
  }, [resultFromOldSystem.data]);

  const renderControlsContract = useCallback(
    (doc: { documentExpiredDate?: Unset }) => {
      if (!ServicePatientFormDocuments.isDocumentOld(doc)) return;
      return (
        <Tooltip title={tp('document-expired')}>
          <IconButton>
            <CloseIcon color={'error'} />
          </IconButton>
        </Tooltip>
      );
    },
    [tp],
  );

  const categories = useMemo(() => {
    return (resultCategories.data || []).map((category) => ({
      id: category.id,
      title: String(category.title),
      icon: category.icon,
      color: category.color,
      documents: signedDocs
        .filter((doc) => doc.formDocument?.formDocumentCategoryID === category.id)
        .map((doc) => ({
          title: doc.formDocument?.title || 'unknown',
          date: doc.signedDate || '',
          url: doc.formDocumentPath || '',
          controls:
            category.formDocumentCategoryType === FormDocumentCategoryType.CONTRACT
              ? renderControlsContract(doc)
              : null,
        })),
    }));
  }, [resultCategories.data, signedDocs, renderControlsContract]);

  const isLoading = resultCategories.isLoading || resultSignedDocs.isLoading;

  return (
    <SectionPaper className={clsx(style.root, className)} title={t('signed-documents-categories')}>
      <div className={style.scroll}>
        <NativeScroll>
          <List
            component={'div'}
            style={{
              padding: 0,
            }}
          >
            {categories.map((category) => {
              return (
                <CategoryItem
                  key={category.id}
                  categoryID={category.id}
                  userPatientProfileID={userPatientProfileID || ''}
                  userPatientProfilePrescriptionID={prescriptionID}
                  title={String(category.title)}
                  icon={category.icon}
                  color={category.color}
                  items={category.documents}
                />
              );
            })}
            <CategoryItem
              categoryID={null}
              userPatientProfileID={userPatientProfileID || ''}
              userPatientProfilePrescriptionID={prescriptionID}
              title={t('documents-from-rapid')}
              icon={ICON_DOCUMENT}
              color={'#666666'}
              items={oldDocuments}
            />
          </List>
        </NativeScroll>
      </div>
      {isLoading && <Loading />}
    </SectionPaper>
  );
};
